import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  ListChecks,
  Search,
  MoreVertical,
  Eye,
  Edit,
  CheckCircle,
  Clock,
  AlertTriangle,
  MapPin,
  Save,
  X,
  Download,
  Upload,
  FileText,
  Trash2,
  Paperclip
} from 'lucide-react';

import { projectService } from '../../../src/services/project.service';
import { punchListService } from '../../../src/services/punch-list.service';
import { fileStorageService } from '../../../src/services/file-storage.service';
import { userService } from '../../../src/services/user.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { notificationService } from '../../../src/services/notification.service';
import { pdfUtils } from '../../../src/utils/pdf.utils';
import { useConfirmDialog } from '../../ui/confirm-dialog';

// Helper function to format dates consistently across components
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

// New Punch List Item Modal Component
function NewPunchListItemModal({ onItemCreated }: { onItemCreated?: () => void }) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    project: '',
    category: '',
    priority: 'medium',
    description: '',
    location: '',
    trade: '',
    assignedTo: '',
    dueDate: '',
    inspectedBy: 'Current User',
    workArea: '',
    phase: '',
    deficiencyType: '',
    costImpact: false,
    scheduleImpact: false,
    photos: [] as Array<{ file_id: string; file_path: string }>,
    notes: ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; size: number; type: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch projects and users when modal opens
  useEffect(() => {
    if (open) {
      loadProjects();
      loadUsers();
    }
  }, [open]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await projectService.getProjects();
      if (response.success && response.data) {
        const projectData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        setProjects(projectData);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      showError({ title: 'Error', message: 'Failed to load projects' });
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      console.log('Users API Response:', response);
      if (response.success && response.data) {
        const userData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('Setting users:', userData);
        setUsers(userData);
      } else {
        console.error('Failed to load users:', response);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showError({ title: 'Error', message: 'Failed to load staff list' });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    try {
      const { documentService } = await import('../../../src/services/document.service');
      const { authService } = await import('../../../src/services/auth.service');
      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      const uploadPromises = Array.from(files).map(async (file) => {
        const uploadResponse = await documentService.uploadFile(file, orgId);

        if (!uploadResponse.success || !uploadResponse.data) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        return {
          id: uploadResponse.data.storage_id,
          name: file.name,
          size: file.size,
          type: file.type,
          filePath: uploadResponse.data.storage_id
        };
      });

      const newFiles = await Promise.all(uploadPromises);
      setUploadedFiles([...uploadedFiles, ...newFiles]);

      const photoObjects = [...formData.photos, ...newFiles.map(f => ({ file_id: f.id, file_path: f.filePath }))];
      setFormData({ ...formData, photos: photoObjects });

      showSuccess({ title: 'Files Uploaded', message: `${newFiles.length} file(s) uploaded successfully` });

    } catch (error: any) {
      console.error('Error uploading files:', error);
      showError({ title: 'Upload Error', message: 'Failed to upload files' });
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
    setFormData({
      ...formData,
      photos: formData.photos.filter(photo => photo.file_id !== fileId)
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Punch list item title is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category selection is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    if (!formData.assignedTo) {
      newErrors.assignedTo = 'Assignment is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const { authService } = await import('../../../src/services/auth.service');
      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;
      const userId = userProfile?.id;

      if (!orgId) {
        showError({ title: 'Error', message: 'Organization ID not found' });
        setIsSaving(false);
        return;
      }

      if (!userId) {
        showError({ title: 'Error', message: 'User ID not found' });
        setIsSaving(false);
        return;
      }

      // Get next item number
      const itemNumberResponse = await punchListService.getNextItemNumber(formData.project);
      if (!itemNumberResponse.success || !itemNumberResponse.data) {
        showError({ title: 'Error', message: 'Failed to generate item number' });
        setIsSaving(false);
        return;
      }

      const payload = {
        organization_id: orgId,
        project_id: formData.project,
        item_number: itemNumberResponse.data.item_number,
        identified_by: userId,
        title: formData.title || undefined,
        description: formData.description,
        location: formData.location,
        work_area: formData.workArea || undefined,
        trade: formData.trade || undefined,
        category: formData.category,
        deficiency_type: formData.deficiencyType || undefined,
        phase: formData.phase || undefined,
        assigned_to: formData.assignedTo || undefined,
        priority: formData.priority as 'low' | 'medium' | 'high' | 'critical',
        due_date: formData.dueDate || undefined,
        cost_impact: formData.costImpact,
        schedule_impact: formData.scheduleImpact,
        photos: formData.photos.length > 0 ? formData.photos : undefined,
        notes: formData.notes || undefined
      };

      const response = await punchListService.createPunchListItem(payload);

      if (response.success) {
        if (formData.assignedTo) {
          notificationService.punchlistItemCreated(formData.assignedTo, formData.title, formData.project);
        }
        showSuccess({
          title: 'Punch Item Created',
          message: `Punch item ${itemNumberResponse.data.item_number} has been created successfully`
        });

        // Reset form
        setFormData({
          title: '',
          project: '',
          category: '',
          priority: 'medium',
          description: '',
          location: '',
          trade: '',
          assignedTo: '',
          dueDate: '',
          inspectedBy: 'Current User',
          workArea: '',
          phase: '',
          deficiencyType: '',
          costImpact: false,
          scheduleImpact: false,
          photos: [],
          notes: ''
        });
        setUploadedFiles([]);

        setOpen(false);

        // Refresh the list after a brief delay to ensure backend has processed
        if (onItemCreated) {
          setTimeout(() => {
            onItemCreated();
          }, 300);
        }
      } else {
        showError({ title: 'Error', message: 'Failed to create punch list item' });
      }
    } catch (error: any) {
      console.error('Error creating punch list item:', error);
      showError({
        title: 'Error',
        message: error?.message || 'Failed to create punch list item'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <ListChecks className="h-4 w-4 mr-2" />
          Add Punch Item
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5" />
            Add Punch List Item
          </DialogTitle>
          <DialogDescription>
            Create a new deficiency or completion item
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Item Information</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="punch-title">Item Title *</Label>
              <Input
                id="punch-title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Paint touch-up required on wall section"
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="punch-project">Project *</Label>
                <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.project_name || project.name || 'Unnamed Project'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="punch-category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="architectural">Architectural</SelectItem>
                    <SelectItem value="structural">Structural</SelectItem>
                    <SelectItem value="mechanical">Mechanical</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="hvac">HVAC</SelectItem>
                    <SelectItem value="landscaping">Landscaping</SelectItem>
                    <SelectItem value="civil">Civil</SelectItem>
                    <SelectItem value="fire-safety">Fire Safety</SelectItem>
                    <SelectItem value="finishes">Finishes</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-sm text-destructive">{errors.category}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deficiency-type">Deficiency Type</Label>
                <Select value={formData.deficiencyType} onValueChange={(value) => setFormData({...formData, deficiencyType: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="incomplete-work">Incomplete Work</SelectItem>
                    <SelectItem value="defective-work">Defective Work</SelectItem>
                    <SelectItem value="damaged-work">Damaged Work</SelectItem>
                    <SelectItem value="non-conforming">Non-Conforming Work</SelectItem>
                    <SelectItem value="missing-item">Missing Item</SelectItem>
                    <SelectItem value="cosmetic">Cosmetic Issue</SelectItem>
                    <SelectItem value="warranty">Warranty Item</SelectItem>
                    <SelectItem value="code-compliance">Code Compliance</SelectItem>
                    <SelectItem value="safety">Safety Issue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="punch-priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="punch-description">Description *</Label>
              <Textarea
                id="punch-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Detailed description of the deficiency or work required..."
                rows={3}
              />
              {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
            </div>
          </div>

          {/* Location & Assignment */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Location & Assignment</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="punch-location">Location *</Label>
                <Input
                  id="punch-location"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g., Building A - Unit 101 - Living Room"
                />
                {errors.location && <p className="text-sm text-destructive">{errors.location}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="work-area">Work Area/Room</Label>
                <Input
                  id="work-area"
                  value={formData.workArea}
                  onChange={(e) => setFormData({...formData, workArea: e.target.value})}
                  placeholder="e.g., South Wall, Corner Detail"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="trade">Trade/Contractor *</Label>
                <Select value={formData.trade} onValueChange={(value) => setFormData({...formData, trade: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general-contractor">General Contractor</SelectItem>
                    <SelectItem value="electrical">Electrical Contractor</SelectItem>
                    <SelectItem value="plumbing">Plumbing Contractor</SelectItem>
                    <SelectItem value="hvac">HVAC Contractor</SelectItem>
                    <SelectItem value="flooring">Flooring Contractor</SelectItem>
                    <SelectItem value="painting">Painting Contractor</SelectItem>
                    <SelectItem value="drywall">Drywall Contractor</SelectItem>
                    <SelectItem value="roofing">Roofing Contractor</SelectItem>
                    <SelectItem value="landscaping">Landscaping Contractor</SelectItem>
                    <SelectItem value="glass-glazing">Glass & Glazing</SelectItem>
                    <SelectItem value="millwork">Millwork</SelectItem>
                    <SelectItem value="specialty">Specialty Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assigned-to">Assigned To *</Label>
                <Select value={formData.assignedTo} onValueChange={(value) => setFormData({...formData, assignedTo: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter((user) => user.user_id).map((user) => {
                      const displayName = user.full_name ||
                        (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
                      const displayText = user.job_title ? `${displayName} - ${user.job_title}` : displayName;
                      return (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {displayText}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {errors.assignedTo && <p className="text-sm text-destructive">{errors.assignedTo}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project-phase">Project Phase</Label>
                <Select value={formData.phase} onValueChange={(value) => setFormData({...formData, phase: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select phase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rough-construction">Rough Construction</SelectItem>
                    <SelectItem value="mep-rough">MEP Rough-In</SelectItem>
                    <SelectItem value="drywall">Drywall & Insulation</SelectItem>
                    <SelectItem value="flooring">Flooring</SelectItem>
                    <SelectItem value="painting">Painting</SelectItem>
                    <SelectItem value="final-finishes">Final Finishes</SelectItem>
                    <SelectItem value="exterior">Exterior Work</SelectItem>
                    <SelectItem value="landscaping">Landscaping</SelectItem>
                    <SelectItem value="final-inspection">Final Inspection</SelectItem>
                    <SelectItem value="warranty">Warranty Period</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="due-date">Target Completion Date</Label>
                <Input
                  id="due-date"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Impact Assessment */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Impact Assessment</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="cost-impact"
                  checked={formData.costImpact}
                  onChange={(e) => setFormData({...formData, costImpact: e.target.checked})}
                  className="rounded border-border"
                />
                <Label htmlFor="cost-impact">Potential Cost Impact</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="schedule-impact"
                  checked={formData.scheduleImpact}
                  onChange={(e) => setFormData({...formData, scheduleImpact: e.target.checked})}
                  className="rounded border-border"
                />
                <Label htmlFor="schedule-impact">Potential Schedule Impact</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Upload Photos/Documents</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => document.getElementById('punch-file-upload-input')?.click()}
              >
                <input
                  id="punch-file-upload-input"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.heic"
                />
                <div className="flex flex-col items-center gap-2">
                  {isUploading ? (
                    <>
                      <Upload className="h-10 w-10 text-primary animate-pulse" />
                      <p className="text-sm font-medium text-primary">Uploading files...</p>
                      <p className="text-xs text-muted-foreground">Please wait</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-gray-400" />
                      <p className="text-sm font-medium">Click to upload or drag and drop</p>
                      <p className="text-xs text-muted-foreground">
                        Photos (PNG, JPG, HEIC), PDF, Word Documents (Max 10MB per file)
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Uploaded Files ({uploadedFiles.length})</Label>
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(file.id)}
                        className="flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="punch-notes">Additional Notes</Label>
              <Textarea
                id="punch-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional details, specifications, or requirements..."
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isSaving} onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Add to Punch List'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Punch List Item Update Modal Component
function PunchListUpdateModal({ item, trigger, onItemUpdated }: { item: any; trigger: React.ReactNode; onItemUpdated?: () => void }) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: item.status || 'open',
    workPerformed: '',
    completionNotes: '',
    completedBy: 'Current User',
    completionDate: new Date().toISOString().split('T')[0],
    qualityVerified: false,
    reinspectionRequired: false
  });

  const handleSubmitUpdate = async () => {
    setIsSaving(true);

    try {
      // If status is completed, call complete API
      if (updateData.status === 'completed') {
        const completeResponse = await punchListService.completePunchListItem(item.punch_item_id, {
          status: 'completed',
          work_performed: updateData.workPerformed,
          completion_notes: updateData.completionNotes || undefined,
          quality_verified: updateData.qualityVerified
        });

        if (completeResponse.success) {
          showSuccess({
            title: 'Item Completed',
            message: 'Punch list item has been marked as completed'
          });
          setOpen(false);
          if (onItemUpdated) onItemUpdated();
        } else {
          showError({ title: 'Error', message: 'Failed to complete punch list item' });
        }
      }
      // If status is verified or rejected, call inspect API
      else if (updateData.status === 'verified' || updateData.status === 'rejected') {
        const inspectResponse = await punchListService.inspectPunchListItem(item.punch_item_id, {
          status: updateData.status,
          quality_verified: updateData.qualityVerified,
          reinspection_required: updateData.reinspectionRequired,
          completion_notes: updateData.completionNotes || undefined
        });

        if (inspectResponse.success) {
          showSuccess({
            title: 'Inspection Complete',
            message: `Punch list item has been ${updateData.status}`
          });
          setOpen(false);
          if (onItemUpdated) onItemUpdated();
        } else {
          showError({ title: 'Error', message: 'Failed to inspect punch list item' });
        }
      }
      // For other status changes, call update API
      else {
        const updateResponse = await punchListService.updatePunchListItem(item.punch_item_id, {
          status: updateData.status as any,
          work_performed: updateData.workPerformed || undefined,
          completion_notes: updateData.completionNotes || undefined
        });

        if (updateResponse.success) {
          showSuccess({
            title: 'Status Updated',
            message: 'Punch list item status has been updated'
          });
          setOpen(false);
          if (onItemUpdated) onItemUpdated();
        } else {
          showError({ title: 'Error', message: 'Failed to update punch list item' });
        }
      }
    } catch (error: any) {
      console.error('Error updating punch list item:', error);
      showError({
        title: 'Error',
        message: error?.message || 'Failed to update punch list item'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Update Punch List Item
          </DialogTitle>
          <DialogDescription>
            Update status and completion details for: {item.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Item Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Category:</span>
                <p className="text-muted-foreground">{item.category}</p>
              </div>
              <div>
                <span className="font-medium">Location:</span>
                <p className="text-muted-foreground">{item.location}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{item.description}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="update-status">Status Update</Label>
            <Select value={updateData.status} onValueChange={(value) => setUpdateData({...updateData, status: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="work-performed">Work Performed</Label>
            <Textarea
              id="work-performed"
              value={updateData.workPerformed}
              onChange={(e) => setUpdateData({...updateData, workPerformed: e.target.value})}
              placeholder="Describe the work performed to address this item..."
              rows={3}
            />
          </div>

          {(updateData.status === 'completed' || updateData.status === 'verified') && (
            <div className="space-y-2">
              <Label htmlFor="completion-notes">Completion Notes</Label>
              <Textarea
                id="completion-notes"
                value={updateData.completionNotes}
                onChange={(e) => setUpdateData({...updateData, completionNotes: e.target.value})}
                placeholder="Additional notes about the completion..."
                rows={2}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="quality-verified"
                checked={updateData.qualityVerified}
                onChange={(e) => setUpdateData({...updateData, qualityVerified: e.target.checked})}
                className="rounded border-border"
              />
              <Label htmlFor="quality-verified">Quality Verified</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="reinspection-required"
                checked={updateData.reinspectionRequired}
                onChange={(e) => setUpdateData({...updateData, reinspectionRequired: e.target.checked})}
                className="rounded border-border"
              />
              <Label htmlFor="reinspection-required">Reinspection Required</Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isSaving} onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmitUpdate} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Updating...' : 'Update Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Punch List View Details Modal Component
function PunchListViewModal({ item, trigger, projects, users }: { item: any; trigger: React.ReactNode; projects?: any[]; users?: any[] }) {
  const [open, setOpen] = useState(false);

  const getProjectName = (projectId: string) => {
    if (!projects) return projectId;
    const project = projects.find(p => p.project_id === projectId);
    return project?.project_name || project?.name || projectId;
  };

  const getUserName = (userId: string) => {
    if (!users) return userId;
    const user = users.find(u => u.user_id === userId);
    if (!user) return userId;
    const displayName = user.full_name ||
      (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
    return user.job_title ? `${displayName} - ${user.job_title}` : displayName;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Punch List Item Details
          </DialogTitle>
          <DialogDescription>
            Item #{item.item_number || item.id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title and Status */}
          <div>
            <Label className="text-xs text-muted-foreground">Title</Label>
            <p className="text-sm font-medium">{item.title || 'Untitled'}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <p className="text-sm">{item.status}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Priority</Label>
              <p className="text-sm">{item.priority}</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs text-muted-foreground">Description</Label>
            <p className="text-sm">{item.description}</p>
          </div>

          {/* Location Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Location</Label>
              <p className="text-sm">{item.location}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Work Area</Label>
              <p className="text-sm">{item.work_area || 'N/A'}</p>
            </div>
          </div>

          {/* Category and Trade */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <p className="text-sm">{item.category}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Trade</Label>
              <p className="text-sm">{item.trade || 'N/A'}</p>
            </div>
          </div>

          {/* Project */}
          <div>
            <Label className="text-xs text-muted-foreground">Project</Label>
            <p className="text-sm">{item.project_id ? getProjectName(item.project_id) : 'N/A'}</p>
          </div>

          {/* Assignments */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Assigned To</Label>
              <p className="text-sm">{item.assigned_to ? getUserName(item.assigned_to) : 'Unassigned'}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Identified By</Label>
              <p className="text-sm">{item.identified_by ? getUserName(item.identified_by) : 'N/A'}</p>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Due Date</Label>
              <p className="text-sm">{formatDate(item.due_date)}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Completed Date</Label>
              <p className="text-sm">{formatDate(item.completed_date)}</p>
            </div>
          </div>

          {/* Impact Assessment */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Cost Impact</Label>
              <p className="text-sm">{item.cost_impact ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Schedule Impact</Label>
              <p className="text-sm">{item.schedule_impact ? 'Yes' : 'No'}</p>
            </div>
          </div>

          {/* Notes */}
          {item.notes && (
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <p className="text-sm whitespace-pre-wrap">{item.notes}</p>
            </div>
          )}

          {/* Photos */}
          {item.photos && item.photos.length > 0 && (
            <div>
              <Label className="text-xs text-muted-foreground">Photos/Attachments ({item.photos.length})</Label>
              <div className="space-y-2 mt-2">
                {item.photos.map((photo: any, index: number) => {
                  const storageId = typeof photo === 'string' ? photo : (photo.file_id || photo.storage_id || photo.file_path);
                  return (
                    <div key={index} className="flex items-center justify-between border rounded p-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Paperclip className="h-3 w-3" />
                        <span>Attachment {index + 1}</span>
                      </div>
                      {storageId && (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-7 px-2"
                            onClick={async () => {
                              try {
                                const res = await fileStorageService.previewFile(storageId);
                                if (res.success && res.data) {
                                  const url = URL.createObjectURL(res.data);
                                  window.open(url, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                                }
                              } catch (e: any) {
                                showError({ title: 'Preview failed', message: e.message });
                              }
                            }}
                          >
                            <Eye className="h-3 w-3 mr-1" />Preview
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2"
                            onClick={async () => {
                              try {
                                const res = await fileStorageService.downloadFile(storageId);
                                if (res.success && res.data) {
                                  const url = URL.createObjectURL(res.data);
                                  const a = document.createElement('a');
                                  a.href = url; a.download = `attachment-${index + 1}`;
                                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                                }
                              } catch (e: any) {
                                showError({ title: 'Download failed', message: e.message });
                              }
                            }}
                          >
                            <Download className="h-3 w-3 mr-1" />Download
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Punch List Actions Menu Component
function PunchListActionsMenu({ item, onUpdate, onDelete, projects, users }: { item: any; onUpdate: () => void; onDelete?: () => void; projects: any[]; users: any[] }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { showConfirm, ConfirmDialog } = useConfirmDialog();

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.project_name || project?.name || projectId;
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.user_id === userId);
    if (!user) return userId;
    const displayName = user.full_name ||
      (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
    return user.job_title ? `${displayName} - ${user.job_title}` : displayName;
  };

  const handleExportToPDF = async () => {
    try {
      const sections = [
        {
          title: 'Item Information',
          content: [
            { label: 'Item Number', value: item.item_number || item.id },
            { label: 'Title', value: item.title || 'Untitled' },
            { label: 'Status', value: item.status },
            { label: 'Priority', value: item.priority },
            { label: 'Category', value: item.category },
            { label: 'Trade', value: item.trade || 'N/A' },
          ]
        },
        {
          title: 'Description',
          content: item.description || 'No description provided'
        },
        {
          title: 'Location Details',
          content: [
            { label: 'Location', value: item.location },
            { label: 'Work Area', value: item.work_area || 'N/A' },
            { label: 'Phase', value: item.phase || 'N/A' },
          ]
        },
        {
          title: 'Assignment',
          content: [
            { label: 'Project', value: item.project_id ? getProjectName(item.project_id) : 'N/A' },
            { label: 'Assigned To', value: item.assigned_to ? getUserName(item.assigned_to) : 'Unassigned' },
            { label: 'Identified By', value: item.identified_by ? getUserName(item.identified_by) : 'N/A' },
          ]
        },
        {
          title: 'Dates & Impact',
          content: [
            { label: 'Due Date', value: formatDate(item.due_date) },
            { label: 'Completed Date', value: formatDate(item.completed_date) },
            { label: 'Cost Impact', value: item.cost_impact ? 'Yes' : 'No' },
            { label: 'Schedule Impact', value: item.schedule_impact ? 'Yes' : 'No' },
          ]
        }
      ];

      if (item.notes) {
        sections.push({
          title: 'Notes',
          content: item.notes
        });
      }

      await pdfUtils.createSimplePDF(
        {
          title: 'Punch List Item',
          subtitle: item.title || 'Untitled',
          documentNumber: item.item_number || item.id,
          date: new Date().toLocaleDateString()
        },
        sections,
        `Punch-List-${item.item_number || item.id}.pdf`
      );

      showSuccess({
        title: 'PDF Exported',
        message: 'Punch list item exported successfully'
      });
    } catch (error: any) {
      console.error('Error exporting PDF:', error);
      showError({
        title: 'Export Failed',
        message: error?.message || 'Failed to export PDF'
      });
    }
  };

  const handleMarkComplete = () => {
    const itemTitle = item.title || `Item #${item.item_number || item.id}`;

    showConfirm({
      title: 'Mark as completed',
      description: `Are you sure you want to mark "${itemTitle}" as completed? This will update the item status.`,
      confirmText: 'Mark Complete',
      cancelText: 'Cancel',
      variant: 'default',
      onConfirm: async () => {
        try {
          const response = await punchListService.completePunchListItem(item.punch_item_id, {
            status: 'completed',
            work_performed: 'Completed via quick action',
            quality_verified: false
          });

          if (response.success) {
            showSuccess({
              title: 'Item Completed',
              message: 'Punch list item marked as completed'
            });
            onUpdate();
          } else {
            showError({ title: 'Error', message: 'Failed to complete punch list item' });
          }
        } catch (error: any) {
          console.error('Error completing item:', error);
          showError({
            title: 'Error',
            message: error?.message || 'Failed to complete punch list item'
          });
        }
      }
    });
  };

  const handleDelete = () => {
    const itemTitle = item.title || `Item #${item.item_number || item.id}`;

    showConfirm({
      title: 'Delete punch list item',
      description: `Are you sure you want to delete "${itemTitle}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          const response = await punchListService.deletePunchListItem(item.punch_item_id);

          if (response.success) {
            showSuccess({
              title: 'Item Deleted',
              message: 'Punch list item has been deleted'
            });
            if (onDelete) {
              onDelete();
            } else {
              onUpdate();
            }
          } else {
            showError({ title: 'Error', message: 'Failed to delete punch list item' });
          }
        } catch (error: any) {
          console.error('Error deleting item:', error);
          showError({
            title: 'Error',
            message: error?.message || 'Failed to delete punch list item'
          });
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  return (
    <>
      <ConfirmDialog />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
        <PunchListViewModal
          item={item}
          projects={projects}
          users={users}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
          }
        />
        <PunchListUpdateModal
          item={item}
          onItemUpdated={onUpdate}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <Edit className="h-4 w-4 mr-2" />
              Update Status
            </DropdownMenuItem>
          }
        />
        {item.status !== 'completed' && item.status !== 'verified' && (
          <DropdownMenuItem onClick={handleMarkComplete}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark Complete
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleExportToPDF}>
          <Download className="h-4 w-4 mr-2" />
          Export Item
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {isDeleting ? 'Deleting...' : 'Delete Item'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}

export function PunchLists() {
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [punchListItems, setPunchListItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // Fetch punch list items, projects, and users
  useEffect(() => {
    loadPunchListItems();
    loadProjects();
    loadUsers();
  }, []);

  const loadPunchListItems = async () => {
    setLoading(true);
    try {
      const response = await punchListService.getPunchListItems();
      console.log('Punch List API Response:', response);
      if (response.success && response.data) {
        // Handle both response formats: { data: [...] } or { data: { data: [...] } }
        const items = Array.isArray(response.data) ? response.data : (response.data.data || []);
        console.log('Loaded punch list items:', items);
        setPunchListItems(items);
      } else {
        console.error('Failed to load punch list items:', response);
      }
    } catch (error) {
      console.error('Error loading punch list items:', error);
      showError({ title: 'Error', message: 'Failed to load punch list items' });
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      if (response.success && response.data) {
        const projectData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        setProjects(projectData);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      if (response.success && response.data) {
        const userData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        setUsers(userData);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Helper functions to get human-readable names
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.project_name || project?.name || projectId;
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.user_id === userId);
    if (!user) return userId;
    const displayName = user.full_name ||
      (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
    return user.job_title ? `${displayName} - ${user.job_title}` : displayName;
  };

  // Sample punch list data for fallback
  const samplePunchListItems = [
    {
      id: "PL-001",
      title: "Paint touch-up required on wall section",
      project: "Residential Complex Alpha",
      projectId: "residential-alpha",
      category: "architectural",
      priority: "medium",
      status: "open",
      description: "Paint scratches and scuffs visible on living room south wall. Touch-up required to match existing finish.",
      location: "Building A - Unit 101 - Living Room",
      workArea: "South Wall",
      trade: "painting",
      assignedTo: "Trade Foreman",
      createdBy: "Quality Inspector",
      createdDate: "2024-03-10",
      dueDate: "2024-03-15",
      phase: "final-finishes",
      deficiencyType: "cosmetic",
      costImpact: false,
      scheduleImpact: false,
      overdue: false
    },
    {
      id: "PL-002",
      title: "Electrical outlet not working in kitchen",
      project: "Hospital Wing Expansion",
      projectId: "hospital-expansion",
      category: "electrical",
      priority: "high",
      status: "in-progress",
      description: "GFCI outlet near kitchen sink not functioning. No power detected during testing.",
      location: "Wing B - Room 205 - Kitchen Area",
      workArea: "Counter Wall",
      trade: "electrical",
      assignedTo: "Electrical Contractor",
      createdBy: "MEP Inspector",
      createdDate: "2024-03-08",
      dueDate: "2024-03-12",
      phase: "final-inspection",
      deficiencyType: "defective-work",
      costImpact: false,
      scheduleImpact: true,
      overdue: true
    },
    {
      id: "PL-003",
      title: "Door frame alignment issue",
      project: "Mixed-Use Development",
      projectId: "mixed-use",
      category: "architectural",
      priority: "high",
      status: "completed",
      description: "Main entrance door frame not properly aligned. Gap visible at top and binding at bottom.",
      location: "Building C - Main Entrance",
      workArea: "Entry Door",
      trade: "millwork",
      assignedTo: "General Contractor",
      createdBy: "Architect",
      createdDate: "2024-03-05",
      dueDate: "2024-03-10",
      completedDate: "2024-03-09",
      phase: "final-finishes",
      deficiencyType: "defective-work",
      costImpact: false,
      scheduleImpact: false,
      overdue: false
    },
    {
      id: "PL-004",
      title: "HVAC vent cover missing",
      project: "School Renovation Project",
      projectId: "school-renovation",
      category: "hvac",
      priority: "medium",
      status: "open",
      description: "Return air vent cover missing in classroom. Ductwork exposed.",
      location: "Classroom 102 - North Wall",
      workArea: "Ceiling Area",
      trade: "hvac",
      assignedTo: "HVAC Contractor",
      createdBy: "Building Inspector",
      createdDate: "2024-03-11",
      dueDate: "2024-03-16",
      phase: "final-finishes",
      deficiencyType: "missing-item",
      costImpact: false,
      scheduleImpact: false,
      overdue: false
    },
    {
      id: "PL-005",
      title: "Concrete crack in loading dock",
      project: "Industrial Warehouse",
      projectId: "industrial-warehouse",
      category: "structural",
      priority: "critical",
      status: "open",
      description: "Hairline crack visible in loading dock concrete slab. Requires structural evaluation and repair.",
      location: "Loading Dock - Bay 3",
      workArea: "Dock Floor",
      trade: "general-contractor",
      assignedTo: "Project Manager",
      createdBy: "Structural Engineer",
      createdDate: "2024-03-12",
      dueDate: "2024-03-14",
      phase: "final-inspection",
      deficiencyType: "defective-work",
      costImpact: true,
      scheduleImpact: true,
      overdue: false
    },
    {
      id: "PL-006",
      title: "Light fixture installation incomplete",
      project: "Residential Complex Alpha",
      projectId: "residential-alpha",
      category: "electrical",
      priority: "low",
      status: "verified",
      description: "Pendant light fixture in dining room not installed. Electrical box prepared but fixture missing.",
      location: "Building A - Unit 102 - Dining Room",
      workArea: "Ceiling Center",
      trade: "electrical",
      assignedTo: "Electrical Contractor",
      createdBy: "Project Manager",
      createdDate: "2024-03-06",
      dueDate: "2024-03-11",
      completedDate: "2024-03-10",
      verifiedDate: "2024-03-11",
      phase: "final-finishes",
      deficiencyType: "incomplete-work",
      costImpact: false,
      scheduleImpact: false,
      overdue: false
    }
  ];

  const filteredItems = punchListItems.filter(item => {
    const matchesSearch = (item.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (item.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (item.location || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (item.item_number || item.id || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject = projectFilter === "all" || item.project_id === projectFilter || item.projectId === projectFilter;
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    const matchesPriority = priorityFilter === "all" || item.priority === priorityFilter;
    return matchesSearch && matchesProject && matchesStatus && matchesCategory && matchesPriority;
  });

  const getStatusBadge = (status: string, overdue?: boolean) => {
    if (overdue) return <Badge variant="destructive">Overdue</Badge>;
    switch (status) {
      case "open": return <Badge variant="secondary">Open</Badge>;
      case "in-progress": return <Badge variant="default" className="bg-blue-600">In Progress</Badge>;
      case "completed": return <Badge variant="default" className="bg-green-600">Completed</Badge>;
      case "verified": return <Badge variant="default" className="bg-green-700">Verified</Badge>;
      case "rejected": return <Badge variant="destructive">Rejected</Badge>;
      case "on-hold": return <Badge variant="outline">On Hold</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "critical": return <Badge variant="destructive">Critical</Badge>;
      case "high": return <Badge className="bg-orange-600">High</Badge>;
      case "medium": return <Badge variant="secondary">Medium</Badge>;
      case "low": return <Badge variant="outline">Low</Badge>;
      default: return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "architectural": return "🏗️";
      case "structural": return "🔩";
      case "mechanical": return "⚙️";
      case "electrical": return "⚡";
      case "plumbing": return "🚰";
      case "hvac": return "🌡️";
      case "landscaping": return "🌿";
      case "civil": return "🛤️";
      case "fire-safety": return "🔥";
      case "finishes": return "🎨";
      default: return "📋";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Punch Lists</h2>
          <p className="text-muted-foreground">Track project completion items and deficiency corrections</p>
        </div>
        <NewPunchListItemModal onItemCreated={loadPunchListItems} />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-blue-600" />
              Total Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{punchListItems.length}</div>
            <p className="text-xs text-blue-600 mt-1">All projects</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Open Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{punchListItems.filter(i => i.status === 'open' || i.status === 'in-progress').length}</div>
            <p className="text-xs text-orange-600 mt-1">Need attention</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{punchListItems.filter(i => i.status === 'completed' || i.status === 'verified').length}</div>
            <p className="text-xs text-green-600 mt-1">Finished items</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Overdue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{punchListItems.filter(i => i.overdue).length}</div>
            <p className="text-xs text-red-600 mt-1">Past due date</p>
          </CardContent>
        </Card>
      </div>

      {/* Punch List Management */}
      <Card>
        <CardHeader>
          <CardTitle>Punch List Register</CardTitle>
          <CardDescription>Manage completion items and deficiency corrections</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search punch list items..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                <SelectItem value="residential-alpha">Residential Complex Alpha</SelectItem>
                <SelectItem value="hospital-expansion">Hospital Wing Expansion</SelectItem>
                <SelectItem value="industrial-warehouse">Industrial Warehouse</SelectItem>
                <SelectItem value="school-renovation">School Renovation</SelectItem>
                <SelectItem value="mixed-use">Mixed-Use Development</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="architectural">Architectural</SelectItem>
                <SelectItem value="structural">Structural</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="plumbing">Plumbing</SelectItem>
                <SelectItem value="hvac">HVAC</SelectItem>
                <SelectItem value="finishes">Finishes</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Punch List Items */}
          <div className="space-y-4">
            {filteredItems.map((item) => (
              <div key={item.punch_item_id || item.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xl">{getCategoryIcon(item.category)}</span>
                      <span className="font-mono text-sm font-medium">{item.item_number || item.id}</span>
                      <h4 className="font-medium">{item.title || 'Untitled'}</h4>
                      {getStatusBadge(item.status, item.overdue)}
                      {getPriorityBadge(item.priority)}
                      {item.cost_impact && <Badge variant="outline" className="text-orange-700 border-orange-300">Cost Impact</Badge>}
                      {item.schedule_impact && <Badge variant="outline" className="text-blue-700 border-blue-300">Schedule Impact</Badge>}
                    </div>

                    <p className="text-sm text-muted-foreground mb-3">{item.description}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <span className="font-medium">Project:</span>
                        <p className="text-muted-foreground">{item.project_id ? getProjectName(item.project_id) : (item.project || 'N/A')}</p>
                      </div>
                      <div>
                        <span className="font-medium">Location:</span>
                        <p className="text-muted-foreground">{item.location}</p>
                      </div>
                      <div>
                        <span className="font-medium">Trade:</span>
                        <p className="text-muted-foreground">{(item.trade || 'N/A').replace('-', ' ')}</p>
                      </div>
                      <div>
                        <span className="font-medium">Due Date:</span>
                        <p className={`text-muted-foreground ${item.overdue ? 'text-red-600 font-medium' : ''}`}>
                          {formatDate(item.due_date || item.dueDate)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-2">
                      <div>
                        <span className="font-medium">Assigned To:</span>
                        <p className="text-muted-foreground">{item.assigned_to ? getUserName(item.assigned_to) : (item.assignedTo || 'Unassigned')}</p>
                      </div>
                      <div>
                        <span className="font-medium">Created By:</span>
                        <p className="text-muted-foreground">{item.identified_by ? getUserName(item.identified_by) : (item.createdBy || 'N/A')}</p>
                      </div>
                      <div>
                        <span className="font-medium">Phase:</span>
                        <p className="text-muted-foreground">{item.phase?.replace('-', ' ')}</p>
                      </div>
                    </div>

                    {item.workArea && (
                      <div className="mt-2">
                        <span className="font-medium text-sm">Work Area: </span>
                        <span className="text-sm text-muted-foreground">{item.workArea}</span>
                      </div>
                    )}

                    {(item.completed_date || item.completedDate || item.inspection_date) && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        {(item.completed_date || item.completedDate) && (
                          <>
                            <span className="font-medium text-green-800">Completed: </span>
                            <span className="text-green-700">{formatDate(item.completed_date || item.completedDate)}</span>
                          </>
                        )}
                        {item.inspection_date && (
                          <>
                            <span className="font-medium text-green-800 ml-4">Inspected: </span>
                            <span className="text-green-700">{formatDate(item.inspection_date)}</span>
                          </>
                        )}
                      </div>
                    )}

                    <div className="mt-2">
                      <Badge variant="outline" className="text-xs">
                        {item.deficiencyType?.replace('-', ' ')}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <PunchListViewModal
                      item={item}
                      projects={projects}
                      users={users}
                      trigger={
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      }
                    />
                    {(item.status === 'open' || item.status === 'in-progress') && (
                      <PunchListUpdateModal
                        item={item}
                        onItemUpdated={loadPunchListItems}
                        trigger={
                          <Button variant="default" size="sm">
                            <Edit className="h-4 w-4 mr-1" />
                            Update
                          </Button>
                        }
                      />
                    )}
                    <PunchListActionsMenu item={item} onUpdate={loadPunchListItems} projects={projects} users={users} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <ListChecks className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No punch list items found</h3>
              <p className="text-muted-foreground">
                {searchQuery || projectFilter !== "all" || statusFilter !== "all" || categoryFilter !== "all" || priorityFilter !== "all"
                  ? "Try adjusting your search criteria or filters"
                  : "No punch list items recorded - great work on quality control!"
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
